import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Youtube,
  Instagram,
  Twitter,
  ArrowUpRight,
  Gauge,
  Sparkles,
  ChevronRight,
  Heart,
  Music2,
} from "lucide-react";
import Nav from "@/components/Nav";
import PageBackground, { PageFooter } from "@/components/PageBackground";

// --- Assets ----------------------------------------------------------------
const MISHENKA_PNG =
  "https://customer-assets.emergentagent.com/job_minimalist-speed/artifacts/fgy4q80v_character%203.png";

const CAR_FRONT =
  "https://customer-assets.emergentagent.com/job_minimalist-speed/artifacts/29wdsx0g_ChatGPT%20Image%2025%20juin%202026%2C%2012_06_52.png";

const BMW_M4_URL =
  "https://www.bmw-m.com/en/fastlane/motorsport/race-cars/bmw-m4-gt3-evo.html";

const SOCIALS = [
  {
    id: "tiktok",
    label: "TikTok",
    Icon: Music2,
    href: "https://www.tiktok.com/@mishenka.iskandera",
  },
  {
    id: "youtube",
    label: "YouTube",
    Icon: Youtube,
    href: "https://www.youtube.com/@MishenkaIskandera",
  },
  { id: "instagram", label: "Instagram", Icon: Instagram, href: "https://www.instagram.com/mishenka.iskandera?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" },
  { id: "x", label: "X", Icon: Twitter, href: "#" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      delay: 0.08 * i,
      ease: [0.21, 0.47, 0.32, 0.98],
    },
  }),
};

export default function Landing() {
  useEffect(() => {
    const i = new Image();
    i.src = CAR_FRONT;
  }, []);

  return (
    <main
      data-testid="landing-page"
      className="relative min-h-screen w-full overflow-hidden text-slate-900"
      style={{
        background:
          "linear-gradient(135deg, #FFF6F7 0%, #FDF8F5 40%, #FFEEF1 100%)",
      }}
    >
      <PageBackground />
      <Nav activePath="/" />

      {/* --- Hero --- */}
      <section
        data-testid="hero-section"
        className="relative z-10 mx-auto grid w-full max-w-[1500px] grid-cols-1 items-center gap-8 px-5 pb-12 pt-2 md:px-12 md:pb-16 lg:grid-cols-12 lg:gap-8 lg:px-16"
      >
        {/* === LEFT: Glass card === */}
        <motion.div
          initial="hidden"
          animate="show"
          className="relative order-2 col-span-1 lg:order-1 lg:col-span-4"
        >
          <div
            data-testid="hero-glass-card"
            className="relative rounded-[28px] border border-white/60 bg-white/55 p-6 shadow-[0_20px_60px_-12px_rgba(244,63,94,0.18)] backdrop-blur-2xl backdrop-saturate-150 sm:p-7 md:p-9"
          >
            {/* Reserved space for the daily Live banner */}
            <div
              className="h-[28px] w-full"
              aria-hidden="true"
              data-testid="live-banner-slot"
            />

            <motion.h1
              custom={1}
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="font-display mt-3 text-3xl font-light leading-[1] tracking-tight text-slate-900 sm:text-4xl md:text-[2.5rem] lg:text-[2.75rem]"
              data-testid="hero-headline"
            >
              SimRacing
              <span className="relative mx-2 italic text-rose-500">
                &amp;
                <span className="absolute -bottom-1 left-0 h-[3px] w-full rounded-full bg-rose-300/60" />
              </span>
              Casual.
            </motion.h1>

            <motion.p
              custom={2}
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="mt-5 max-w-md text-sm font-light leading-relaxed text-slate-600 md:text-base"
              data-testid="hero-subhead"
            >
              I&apos;m Mishenka — a local race car driver aiming to be a
              national champion. Strap into the BMW M4 GT3 with me every
              evening for clean apexes, soft chats, and a brighter side of
              motorsport.
            </motion.p>

            <motion.div
              custom={3}
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="mt-7 flex flex-wrap items-center gap-3"
            >
              <a
                href="https://www.youtube.com/@MishenkaIskandera"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="primary-cta-youtube"
                className="group inline-flex items-center gap-2 rounded-full bg-rose-500 px-6 py-3 text-sm font-medium text-white shadow-lg shadow-rose-500/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-rose-600 hover:shadow-rose-500/45 md:px-7 md:py-3.5"
              >
                <Youtube className="h-4 w-4" />
                YouTube
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </a>
              <a
                href="https://sociabuzz.com/mishenkavt/donate"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="secondary-cta-support"
                className="group inline-flex items-center gap-2 rounded-full border border-slate-900/15 bg-white/60 px-6 py-3 text-sm font-medium text-slate-800 backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 hover:border-rose-300/60 hover:bg-white/90 hover:text-rose-600 md:px-7 md:py-3.5"
              >
                <Heart className="h-4 w-4 text-rose-500" />
                Support Me
                <ChevronRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </a>
            </motion.div>

            <motion.div
              custom={4}
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="mt-8 h-px w-full bg-gradient-to-r from-transparent via-slate-300/60 to-transparent"
            />

            <motion.div
              custom={5}
              variants={fadeUp}
              initial="hidden"
              animate="show"
              className="mt-5 flex flex-wrap items-center justify-between gap-3"
            >
              <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">
                Follow the lap
              </div>
              <div className="flex items-center gap-2">
                {SOCIALS.map(({ id, label, Icon, href }) => (
                  <a
                    key={id}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    data-testid={`social-link-${id}`}
                    className="group flex h-10 w-10 items-center justify-center rounded-full border border-white/60 bg-white/55 text-slate-700 backdrop-blur-md transition-all duration-300 hover:-translate-y-0.5 hover:border-rose-200 hover:bg-white/95 hover:text-rose-500 hover:shadow-md hover:shadow-rose-500/15 md:h-11 md:w-11"
                  >
                    <Icon className="h-[17px] w-[17px] md:h-[18px] md:w-[18px]" strokeWidth={1.75} />
                  </a>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Floating signature card */}
          <motion.div
            initial={{ opacity: 0, y: 24, rotate: -2 }}
            animate={{ opacity: 1, y: 0, rotate: -2 }}
            transition={{ duration: 0.9, delay: 0.7, ease: "easeOut" }}
            className="absolute -bottom-6 right-4 hidden rounded-2xl border border-white/60 bg-white/75 px-4 py-3 shadow-xl shadow-rose-500/10 backdrop-blur-xl lg:flex lg:items-center lg:gap-3"
            data-testid="floating-signature"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-50 ring-2 ring-white">
              <Sparkles className="h-5 w-5 text-rose-500" />
            </div>
            <div className="leading-tight">
              <div className="font-display text-sm font-semibold text-slate-900">
                Mishenka Iskandera
              </div>
              <div className="text-[11px] text-slate-500">
                VTuber · Aspiring national champion
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* === CENTER: BMW M4 GT3 (clickable) === */}
        <div className="relative order-1 col-span-1 flex flex-col items-center gap-4 lg:order-2 lg:col-span-4">
          <div className="relative aspect-[16/10] w-full max-w-[520px]">
            <div className="absolute bottom-[8%] left-1/2 -z-10 h-6 w-[70%] -translate-x-1/2 rounded-full bg-slate-900/20 blur-2xl" />

            <a
              href={BMW_M4_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Learn more about the BMW M4 GT3"
              data-testid="car-link"
              className="group relative block h-full w-full cursor-pointer outline-none"
            >
              <motion.img
                src={CAR_FRONT}
                alt="BMW M4 GT3"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.9, ease: "easeOut" }}
                className="car-float h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                data-testid="bmw-render-image"
              />
            </a>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full border border-white/60 bg-white/70 px-4 py-1.5 text-[10px] uppercase tracking-[0.28em] text-slate-700 shadow-md shadow-rose-500/10 backdrop-blur-xl"
              data-testid="car-name-badge"
            >
              BMW M4 GT3 · #787
            </motion.div>
          </div>

          {/* "more about the M4" link */}
          <motion.a
            href={BMW_M4_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="more-about-m4-link"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="group inline-flex items-center gap-2 font-display text-sm font-medium text-slate-700 transition-colors duration-300 hover:text-rose-500"
          >
            <span className="relative">
              more about the M4
              <span className="absolute -bottom-0.5 left-0 h-px w-full origin-left scale-x-0 bg-rose-500 transition-transform duration-300 group-hover:scale-x-100" />
            </span>
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </motion.a>

          {/* Now-driving stat chip */}
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="flex items-center gap-3 rounded-2xl border border-white/55 bg-white/60 px-4 py-3 shadow-xl shadow-rose-500/10 backdrop-blur-2xl backdrop-saturate-150"
            data-testid="stat-card-now-driving"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-50">
              <Gauge className="h-5 w-5 text-rose-500" strokeWidth={2} />
            </div>
            <div className="leading-tight">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
                Now driving
              </div>
              <div className="font-display text-sm font-semibold text-slate-900">
                BMW M4 GT3
              </div>
              <div className="text-[11px] text-slate-500">M Motorsport · #787</div>
            </div>
          </motion.div>
        </div>

        {/* === RIGHT: Mishenka character === */}
        <div className="relative order-3 col-span-1 flex items-end justify-center lg:col-span-4">
          <div className="relative aspect-[3/4] w-full max-w-[360px] sm:max-w-[400px] lg:max-w-[440px]">
            <div
              className="absolute inset-0 -z-10"
              style={{
                background:
                  "radial-gradient(closest-side, rgba(244,63,94,0.22), rgba(244,63,94,0.06) 55%, transparent 75%)",
              }}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.2, ease: "easeOut" }}
              className="absolute left-1/2 top-1/2 -z-10 h-[78%] w-[78%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-rose-200/70"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.2, delay: 0.35, ease: "easeOut" }}
              className="absolute left-1/2 top-1/2 -z-10 h-[92%] w-[92%] -translate-x-1/2 -translate-y-1/2 rounded-full border border-rose-100/80"
            />

            <motion.img
              src={MISHENKA_PNG}
              alt="Mishenka Iskandera"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.25, ease: "easeOut" }}
              className="character-float relative h-full w-full object-contain object-bottom drop-shadow-[0_20px_40px_rgba(244,63,94,0.18)]"
              data-testid="hero-character"
            />

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.9 }}
              className="absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap text-center"
            >
              <div className="text-[10px] uppercase tracking-[0.32em] text-rose-500">
                ミシェンカ · Iskandera
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <PageFooter />
    </main>
  );
}
